// What each chain can and cannot do, in one table.
//
// Before this existed the answers were spelled out as chain literals wherever
// they were needed — `transfers.ts` alone asks forty times whether a chain is
// eth, trx or btc. Most of those are not really asking about the chain; they
// are asking one of four questions: who pays the gas, how a transfer is built,
// whether the chain carries contract tokens at all, and whether it may take
// part in the stablecoin flows (cards, staking, presale). Adding a chain then
// means finding every site that happens to ask, and the ones you miss fail
// quietly — a chain silently treated as sponsored, or as Tron.
//
// So: the per-chain facts live here, features ask by capability, and a new
// chain is a new row. `Record<Chain, …>` makes the row mandatory.

import { isNativeToken } from './constants';
import type { AnyTokenSymbol, Chain } from './types';

/**
 * Who pays the network fee, and by what mechanism.
 *
 * Named after the mechanism rather than the intent. `sponsored` and `relayed`
 * read like policies, but each one is really the name of one implementation —
 * and the send route branches on them. A new chain given the nearest-sounding
 * value would be routed through that implementation: a Solana transfer marked
 * `relayed` would have gone down Tron's energy-rental path, which is exactly
 * the class of mistake the capability table exists to prevent.
 *
 * - `evm-bundler`  ERC-4337 UserOp with gas from an Alchemy Gas Manager
 *                  policy; the user never holds gas and is billed a service
 *                  fee on top.
 * - `tron-energy`  energy rented per transfer from refee and billed on top.
 * - `fee-payer`    a project key co-signs as the transaction's fee payer
 *                  (Solana's model). Not implemented yet.
 * - `self-paid`    the fee comes out of the sender's own balance on that
 *                  chain. Bitcoin works this way, and so will Monero, Zcash
 *                  and HyperEVM — none has a meta-transaction to relay.
 */
export type GasModel = 'evm-bundler' | 'tron-energy' | 'fee-payer' | 'self-paid';

/** How a transfer is constructed and signed. */
export type AccountModel =
  // Balance on an address, signed with its key: EVM, Tron, Solana.
  | 'eoa'
  // Spend selected outputs and return change: Bitcoin, transparent Zcash.
  | 'utxo'
  // The wallet is a contract that must be deployed before it can send: TON W5.
  | 'contract'
  // Balance is found by scanning the chain with a view key: Monero.
  | 'private';

export interface ChainCaps {
  accountModel: AccountModel;
  /** Gas model for this chain's contract tokens (USDT/USDC). */
  tokenGas: GasModel;
  /** Gas model for this chain's own coin. Sponsoring a coin transfer would
   *  mean topping the sender up with the very thing being sent, so this is
   *  `self-paid` everywhere today. */
  nativeGas: GasModel;
  /** Whether the chain hosts contract tokens — `TOKENS[chain]` exists. */
  hasContractTokens: boolean;
  /**
   * Whether the chain may carry the stablecoin flows: card top-up, staking
   * deposits, presale payments. False on a coin-only chain, which has nothing
   * those flows can accept, and would stay false on a chain whose assets the
   * AML provider or the card issuer will not take.
   */
  stablecoinRails: boolean;
}

export const CHAIN_CAPS: Record<Chain, ChainCaps> = {
  eth: {
    accountModel: 'eoa',
    tokenGas: 'evm-bundler',
    nativeGas: 'self-paid',
    hasContractTokens: true,
    stablecoinRails: true,
  },
  trx: {
    accountModel: 'eoa',
    tokenGas: 'tron-energy',
    nativeGas: 'self-paid',
    hasContractTokens: true,
    stablecoinRails: true,
  },
  bsc: {
    // Same account model and the same key as Ethereum — a BSC address IS the
    // user's Ethereum address.
    //
    // Self-paid by decision, not by necessity: Alchemy's Gas Manager does
    // cover BNB Smart Chain, so this could be sponsored like Ethereum. Of the
    // chains added after eth/trx/btc, the user pays their own gas — which on
    // BSC costs them a few cents a transfer and saves us needing a gas policy
    // at all. It also means no service fee is charged here: a self-paid send
    // has no treasury leg.
    accountModel: 'eoa',
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    hasContractTokens: true,
    stablecoinRails: true,
  },
  sol: {
    // An address is an ed25519 public key, but it still holds its own balance
    // and signs for itself, so the account model matches the EVM chains.
    //
    // Self-paid, like every chain added after eth/trx/btc. `fee-payer` — a
    // project key co-signing as the transaction's fee payer — is Solana's
    // sponsorship shape and stays defined for the day that is wanted, but
    // nothing selects it.
    //
    // Self-paid costs the sender 5000 lamports a signature, plus ~0.00204 SOL
    // of rent when the recipient has no account for the token yet. Receiving
    // needs no SOL at all: whoever sends pays for the account they create.
    accountModel: 'eoa',
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    hasContractTokens: true,
    stablecoinRails: true,
  },
  hyperevm: {
    // EVM, so the same address and the same signer as Ethereum. Both gas
    // models are self-paid: Hyperliquid has no ERC-4337 bundler behind an
    // Alchemy Gas Manager policy, and the decision for this chain was that
    // users pay their own HYPE.
    accountModel: 'eoa',
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    hasContractTokens: true,
    stablecoinRails: true,
  },
  ton: {
    // The one chain here whose wallet is a CONTRACT. A TON address is the hash
    // of the wallet's initial state, not of a public key, and the contract has
    // to be deployed before it can send. Receiving needs no deployment, and
    // deployment rides along with the first outgoing transfer, so this costs
    // the user nothing extra — but it is why `accountModel` is its own value
    // rather than 'eoa': anything that assumes an address can sign the moment
    // it is derived is wrong here.
    accountModel: 'contract',
    // Self-paid, like every chain added after eth/trx/btc. TON has no
    // ERC-4337-shaped relay for us to sponsor through; W5 does support
    // gas-free extensions, but that needs a sponsor contract of our own and
    // the decision for these chains was that users pay their own gas.
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    // USDT only — see the TOKENS note. `hasContractTokens` asks whether the
    // chain has a token table at all, not whether it has both stablecoins.
    hasContractTokens: true,
    stablecoinRails: true,
  },
  zec: {
    // Transparent Zcash: spend selected outputs and return change, like
    // Bitcoin. The resemblance stops at the address and the model — a v5
    // transaction (ZIP-225) is its own serialization with its own sighash, so
    // the Bitcoin signer cannot build one. Hence no send mode below.
    accountModel: 'utxo',
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    hasContractTokens: false,
    stablecoinRails: false,
  },
  xmr: {
    // The only chain here where a balance cannot be looked up. Monero hides
    // amounts and recipients, and has no address index by design — there is no
    // "what does this address hold" question the network can answer. The only
    // way to find your money is to scan every block with your view key, which
    // is why the account model is its own value and why the balance for this
    // chain is computed on the DEVICE and never by the server.
    accountModel: 'private',
    // The fee comes out of the sender's own balance, like Bitcoin's. There is
    // nothing to sponsor: a Monero transaction is built and signed entirely by
    // the client, and no relay exists to pay for one.
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    // No contracts at all.
    hasContractTokens: false,
    stablecoinRails: false,
  },
  btc: {
    accountModel: 'utxo',
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    hasContractTokens: false,
    stablecoinRails: false,
  },
  xrp: {
    // An address holds its own balance and signs for itself, so the account
    // model is the same as the EVM chains' — but ONE THING IS UNLIKE EVERY
    // OTHER CHAIN HERE: an XRPL account must hold a reserve to exist at all.
    //
    // That has two consequences nothing in this table can express, so they
    // live where the money is counted instead:
    //   - A payment to an address that has never been funded must carry at
    //     least the base reserve, or the ledger rejects it.
    //   - The reserve can never be sent. "Balance" and "what I can send" are
    //     different numbers; xrpSpendableDrops is the one the UI must show.
    //
    // It is not its own accountModel because the model — one key, one
    // balance, signs for itself — really is 'eoa'. Inventing a value would
    // route XRP through a signer that does not exist, which is the exact
    // mistake the comment at the top of this file warns about.
    accountModel: 'eoa',
    // Self-paid, like every chain added after eth/trx/btc. The fee is 10 drops
    // — a thousandth of a cent — so there is nothing worth sponsoring, and no
    // meta-transaction on this ledger to sponsor it through.
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    // Issued currencies exist on XRPL, but they need a trust line the holder
    // sets up first, and no USDT/USDC we accept lives here. Coin only.
    hasContractTokens: false,
    stablecoinRails: false,
  },
  doge: {
    // Bitcoin's model without SegWit: select outputs, return change, sign each
    // input against the whole previous transaction rather than its value alone.
    // That last difference is why the signer cannot simply be pointed at a new
    // network constant — see dogeFeeKoinu and the doge adapter.
    accountModel: 'utxo',
    tokenGas: 'self-paid',
    nativeGas: 'self-paid',
    hasContractTokens: false,
    stablecoinRails: false,
  },
};

export function chainCaps(chain: Chain): ChainCaps {
  return CHAIN_CAPS[chain];
}

/** Who pays for this exact transfer. Native sends are self-paid even on a
 *  chain whose token transfers are sponsored. */
export function gasModelFor(chain: Chain, token: AnyTokenSymbol): GasModel {
  const caps = CHAIN_CAPS[chain];
  return isNativeToken(token) ? caps.nativeGas : caps.tokenGas;
}

/**
 * Whether the project covers the network fee for this transfer, so the user
 * is charged a service fee on top instead of holding gas.
 *
 * This says what the chain supports, not whether the keys for it are
 * provisioned — callers still check their own configuration before relying on
 * a relayer or a bundler.
 */
export function isFeeSponsored(chain: Chain, token: AnyTokenSymbol): boolean {
  return gasModelFor(chain, token) !== 'self-paid';
}

/** True when the sender needs a balance of the chain's own coin to cover the
 *  fee — the UI must warn before the send, not after it fails. */
export function needsNativeForFee(chain: Chain, token: AnyTokenSymbol): boolean {
  return gasModelFor(chain, token) === 'self-paid';
}

/** Whether this chain can take part in card top-up, staking and presale. */
export function hasStablecoinRails(chain: Chain): boolean {
  return CHAIN_CAPS[chain].stablecoinRails;
}

/**
 * How the client executes a self-paid coin send, by chain.
 *
 * `POST /v1/transfers` answers with a `mode` and the TMA switches on it to pick
 * a signer. The API wrote these strings inline in two places and the TMA listed
 * them again in two more, so a new chain's coin send could reach the client
 * under a mode nothing handled — or, worse, fall through the API's chain checks
 * into the sponsored path. One table, and `Record<Chain, …>` makes the row
 * mandatory on both sides.
 */
export type NativeSendMode =
  | 'client-native-eth'
  | 'client-native-trx'
  | 'client-native-bnb'
  | 'client-native-hype'
  | 'client-native-sol'
  | 'client-native-ton'
  | 'client-btc'
  | 'client-zec'
  | 'client-xmr'
  | 'client-doge'
  | 'client-xrp';

/**
 * `null` means the client has no signer for that chain's coin yet.
 *
 * The alternative was to invent a mode string for it, which the API would then
 * answer with and the client would not recognise — leaving the transfer row
 * pending forever with no error anyone could see. Nullable makes the gap
 * explicit, and the route turns it into a refusal.
 */
export const NATIVE_SEND_MODE: Record<Chain, NativeSendMode | null> = {
  eth: 'client-native-eth',
  trx: 'client-native-trx',
  // Its own mode even though the signer is the same plain EVM value tx: the
  // client has to broadcast through the BSC proxy, not the Ethereum one.
  bsc: 'client-native-bnb',
  sol: 'client-native-sol',
  hyperevm: 'client-native-hype',
  ton: 'client-native-ton',
  btc: 'client-btc',
  zec: 'client-zec',
  xmr: 'client-xmr',
  // Its own mode rather than reusing 'client-btc': the signer differs (legacy
  // P2PKH, every input needs its full previous transaction) and so does the
  // broadcast endpoint. One mode for both would have sent Dogecoin through
  // Bitcoin's proxy.
  doge: 'client-doge',
  xrp: 'client-xrp',
};

export const NATIVE_SEND_MODES: readonly NativeSendMode[] = Object.values(
  NATIVE_SEND_MODE,
).filter((m): m is NativeSendMode => m != null);

/**
 * How the client sends a CONTRACT token whose gas the sender pays themselves.
 *
 * The existing modes split the wrong way for this: the coin modes sign a value
 * transfer, and sponsorship covers tokens. A chain where tokens exist but
 * nothing sponsors them fits neither, and without a mode of its own its
 * stablecoins are simply unsendable.
 *
 * Keyed per chain rather than one constant: `client-erc20` is one transaction
 * shape that every EVM chain shares, but an SPL transfer is a different
 * transaction entirely — a different signature scheme, and a token account to
 * create when the recipient has none. A single mode would have sent Solana's
 * tokens through the EVM signer. `null` means the chain either sponsors its
 * tokens or has none.
 */
export type SelfPaidTokenMode = 'client-erc20' | 'client-spl' | 'client-jetton';

export const SELF_PAID_TOKEN_MODE: Record<Chain, SelfPaidTokenMode | null> = {
  eth: null, // sponsored
  trx: null, // sponsored
  bsc: 'client-erc20',
  sol: 'client-spl',
  hyperevm: 'client-erc20',
  // A jetton transfer is not an ERC-20 one: the balance lives in a per-holder
  // contract the master deploys, so sending means messaging that contract, and
  // the recipient's may not exist yet. Its own mode, like Solana's.
  ton: 'client-jetton',
  btc: null, // no contract tokens
  zec: null, // no contract tokens
  xmr: null, // no contract tokens
  doge: null, // no contract tokens
  xrp: null, // issued currencies need a trust line; none we accept
};

export const SELF_PAID_TOKEN_MODES: readonly SelfPaidTokenMode[] = [
  ...new Set(Object.values(SELF_PAID_TOKEN_MODE).filter((m): m is SelfPaidTokenMode => m != null)),
];

/**
 * Zcash's transparent fee, in zatoshi, under ZIP 317.
 *
 * Here rather than only in the client because the fee quote is served by the
 * API and the fee actually paid is computed by the signer — two numbers the
 * user compares, so they have to come from one formula. Derived from the
 * signing library's own getFee() across eight input/output combinations and
 * pinned against it by a test, so a change there fails loudly instead of
 * quoting one fee and charging another.
 */
export function zcashFeeZatoshi(inputs: number, outputs: number, hasMemo = false): number {
  const actions = Math.max(1, inputs, outputs);
  return 10_000 * actions + (hasMemo ? 10_000 : 0);
}

/**
 * Whether a chain carries a destination memo, and in what form.
 *
 * A table rather than `chain === 'xrp'` for the usual reason: a chain added
 * later that needs one would otherwise have its memo silently dropped, and a
 * payment to an exchange without its tag is not credited to anybody. TON's
 * comment and Monero's payment ID belong here when their send paths learn to
 * attach them; today neither does, so they are null and say so.
 *
 * Only relevant when sending TO a shared address. Our own receiving addresses
 * are one per user, so an incoming payment never needs one.
 */
export type MemoKind = 'xrp-destination-tag';

export const MEMO_KIND: Record<Chain, MemoKind | null> = {
  eth: null,
  trx: null,
  bsc: null,
  sol: null,
  hyperevm: null,
  // TON transfers can carry a text comment, which is what exchanges want
  // here — but tonSend builds a plain transfer today, so claiming support
  // would mean accepting a comment and dropping it.
  ton: null,
  btc: null,
  zec: null,
  // Monero uses an integrated address or a payment ID. Neither is a string we
  // can pass through this field unchanged, so it is not a memo chain yet.
  xmr: null,
  doge: null,
  xrp: 'xrp-destination-tag',
};

/** Whether this chain can carry a memo at all. */
export function supportsMemo(chain: Chain): boolean {
  return MEMO_KIND[chain] !== null;
}

/**
 * Parse a user-typed memo into an XRPL DestinationTag.
 *
 * Deliberately strict. The tag is a uint32 and the ledger has no way to tell a
 * mistyped one from a valid one — it delivers to whatever account the tag
 * names at the exchange, which may be somebody else's. So: digits only, no
 * sign, no decimal point, no leading '+', and inside the range. `Number()`
 * would have accepted '1e3', ' 12 ' and '0x10' and turned each into something
 * the user did not type.
 */
export function parseXrpDestinationTag(memo: string): number {
  const raw = memo.trim();
  if (!/^[0-9]{1,10}$/.test(raw)) throw new Error('memo_invalid');
  const tag = Number(raw);
  if (!Number.isInteger(tag) || tag < 0 || tag > 0xffffffff) throw new Error('memo_invalid');
  return tag;
}

/**
 * Dogecoin fee rates and dust limit, in koinu (1 DOGE = 100,000,000 koinu).
 *
 * Here for the same reason as zcashFeeZatoshi: the API quotes a fee and the
 * client pays one, and the user compares the two numbers.
 *
 * The values come from Dogecoin Core's own doc/fee-recommendation.md, not from
 * a fee API. That matters more here than on any other chain: Blockchair's
 * `suggested_transaction_fee_per_byte_sat` for Dogecoin reads 500000, which is
 * 500x the recommended rate — a 226-byte transfer quoted at 1.13 DOGE instead
 * of 0.00226. A Bitcoin-shaped fee estimator applied to this chain overcharges
 * by two and a half orders of magnitude, so we do not ask one.
 */
export const DOGE_FEE_PER_KB_KOINU = 1_000_000n; // 0.01 DOGE/kB, recommended
export const DOGE_RELAY_MIN_PER_KB_KOINU = 100_000n; // 0.001 DOGE/kB, relay floor

/**
 * Dust limit: an output below this is rejected by the wallet and by relays.
 *
 * 0.01 DOGE. In units that is 1,000,000 koinu against Bitcoin's 546 satoshi,
 * so BTC_DUST_SAT cannot be reused — change that Bitcoin would happily create
 * is unspendable here, and an output Bitcoin calls generous is dust.
 */
export const DOGE_DUST_KOINU = 1_000_000n;

/** Fee in koinu for a transaction of `vbytes`, at the recommended rate.
 *  Dogecoin has no SegWit, so a byte is a byte — there is no witness discount
 *  and vsize equals size. */
export function dogeFeeKoinu(bytes: number, perKb: bigint = DOGE_FEE_PER_KB_KOINU): bigint {
  if (!Number.isFinite(bytes) || bytes <= 0) throw new Error('doge fee: bad size');
  const rate = perKb < DOGE_RELAY_MIN_PER_KB_KOINU ? DOGE_RELAY_MIN_PER_KB_KOINU : perKb;
  // Round up: a fee a fraction of a koinu short is a transaction relays drop.
  return (BigInt(Math.ceil(bytes)) * rate + 999n) / 1000n;
}

/** Whether this transfer is a token the sender pays their own gas for. */
export function isSelfPaidTokenSend(chain: Chain, token: AnyTokenSymbol): boolean {
  return !isNativeToken(token) && gasModelFor(chain, token) === 'self-paid';
}
